import { useState, useEffect, useCallback } from "react";
import React from "react";
import { Plus, Filter, Search, CheckCheck, Trash2 } from "lucide-react";
import axiosInstance from "../../API/interceptor";
import CreateTaskDrawer from "../../components/MyTask/CreateTaskDrawer";

import TodoListTab, { TABS, TodoFilterPanel, AddTodoModal } from "./TodoListTab";
import GroupTab from "./Groups";

/* ── Date formatter UTC → local "14 Apr 2026, 23:14" ── */
function fmtDate(v) {
  if (!v || v === "None") return "—";
  try {
    let s = String(v).trim();
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");
    if (!s.endsWith("Z") && !s.includes("+")) s += "Z";
    const d = new Date(s);
    if (isNaN(d)) return v;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const pad = n => String(n).padStart(2, "0");
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return v; }
}

export default function TodoListPage() {
  const [activeTab,     setActiveTab]     = useState("Todo List");
  const [search,        setSearch]        = useState("");
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [filterOpen,    setFilterOpen]    = useState(false);
  const [activeFilters, setActiveFilters] = useState({ dateFrom: "", dateTo: "", funcCat: "" });
  const [convertDraft,  setConvertDraft]  = useState(null);
  const [selected,      setSelected]      = useState([]);
  const [todos,         setTodos]         = useState([]);
  const [loading,       setLoading]       = useState(false);

  /* ════ LOAD TODOS ════ */
  const loadTodos = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/task_assignment/todo_list", { params });
      const raw = Array.isArray(res.data?.todo_list) ? res.data.todo_list
                : Array.isArray(res.data)            ? res.data : [];
      setTodos(raw.map(t => ({
        id:      t._id,
        title:   t.task_name || "",
        details: t.task_details || "",
        status:  t.status || "open",
        group:   Array.isArray(t.functional_categories) ? t.functional_categories : [],
        created: fmtDate(t.created_at),
        raw:     t,
      })));
    } catch { setTodos([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === "Todo List") loadTodos();
  }, [activeTab, loadTodos]);

  /* ── Add new task ── */
  const handleAddTodo = ({ taskName, details, category, task_id }) => {
    const now = new Date();
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const pad = n => String(n).padStart(2, "0");
    setTodos(prev => [{
      id:      task_id || Date.now(),
      title:   taskName,
      details: details || "",
      status:  "open",
      group:   category || [],
      created: `${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}, ${pad(now.getHours())}:${pad(now.getMinutes())}`,
    }, ...prev]);
  };

  /* ── Bulk complete ── */
  const handleCompleteSelected = async () => {
    const openSelected = selected.filter(id => {
      const t = todos.find(t => t.id === id);
      return t && t.status !== "complete" && t.status !== "done";
    });
    if (openSelected.length === 0) return;
    if (!window.confirm(`Complete ${openSelected.length} selected task(s)?`)) return;
    try {
      await axiosInstance.post("/complete_multiple", { "task_ids[]": openSelected });
      setTodos(prev => prev.map(t => openSelected.includes(t.id) ? { ...t, status: "complete" } : t));
      setSelected([]);
    } catch { /* silent */ }
  };

  /* ── Bulk delete ── */
  const handleDeleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected task(s)? This cannot be undone.`)) return;
    try {
      await axiosInstance.post("/delete_multiple", { "task_ids[]": selected });
      setTodos(prev => prev.filter(t => !selected.includes(t.id)));
      setSelected([]);
    } catch { /* silent */ }
  };

  /* ── Apply filters ── */
  const handleApplyFilters = (f) => {
    setActiveFilters(f);
    const params = {};
    if (f.dateFrom) params.date_from  = f.dateFrom;
    if (f.dateTo)   params.date_to    = f.dateTo;
    if (f.funcCat)  params.categories = f.funcCat;
    loadTodos(params);
  };

  const handleClearFilters = () => {
    setActiveFilters({ dateFrom: "", dateTo: "", funcCat: "" });
    loadTodos();
  };

  /* ── Active filter pills ── */
  const filterPills = [
    ...(activeFilters.dateFrom ? [{ label: `From: ${activeFilters.dateFrom}`, key: "dateFrom" }] : []),
    ...(activeFilters.dateTo   ? [{ label: `To: ${activeFilters.dateTo}`,     key: "dateTo"   }] : []),
    ...(activeFilters.funcCat  ? [{ label: activeFilters.funcCat,              key: "funcCat"  }] : []),
  ];

  const removePill = (key) => {
    const newF = { ...activeFilters, [key]: "" };
    setActiveFilters(newF);
    const params = {};
    if (newF.dateFrom) params.date_from  = newF.dateFrom;
    if (newF.dateTo)   params.date_to    = newF.dateTo;
    if (newF.funcCat)  params.categories = newF.funcCat;
    loadTodos(params);
  };

  /* ── Stat pills ── */
  const statPills = [
    { label: "Open",      count: todos.filter(t => t.status === "open" || t.status === "pending").length,           color: "#D97406" },
    { label: "Completed", count: todos.filter(t => t.status === "complete" || t.status === "done").length,          color: "#059669" },
  ];

  /* ════ RENDER ════ */
  return (
    <div className="h-auto bg-slate-50 font-sans rounded-lg">

      {convertDraft && (
        <CreateTaskDrawer
          open={true}
          resumeDraftData={null}
          todoConvert={convertDraft}
          onClose={() => setConvertDraft(null)}
          onTaskCreated={() => { setConvertDraft(null); loadTodos(); }}
        />
      )}

      {drawerOpen && (
        <AddTodoModal
          onClose={() => setDrawerOpen(false)}
          onAdd={handleAddTodo}
        />
      )}

      <TodoFilterPanel
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

        {/* ── Tab header ── */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="px-4">
            <div className="flex items-center justify-between gap-1 border-b border-slate-100">
              <div className="flex overflow-x-auto scrollbar-hide">
                {TABS.map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3.5 text-sm font-semibold whitespace-nowrap bg-transparent cursor-pointer transition-all -mb-px border-b-2 ${
                      activeTab === tab ? "text-violet-600 font-bold border-violet-600" : "text-slate-500 hover:text-slate-700 border-transparent"}`}>
                    {tab}
                  </button>
                ))}
              </div>

              {/* Stat pills — only on Todo List tab */}
              {activeTab === "Todo List" && (
                <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                  {statPills.map(p => (
                    <span key={p.label} className="text-xs font-bold px-3 py-1 rounded-full border"
                      style={{ background: `${p.color}12`, color: p.color, borderColor: `${p.color}30` }}>
                      {p.label}: {loading ? "…" : p.count}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── PERSONAL TAB ── */}
        {activeTab === "Todo List" && (
          <>
            {/* Action bar */}
            <div className="flex items-center justify-between px-4 py-3 gap-3 flex-wrap border-b border-gray-50">
              {/* Search */}
              <div className="relative flex-1 max-w-[440px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search tasks..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none text-slate-700 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.05)]" />
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setFilterOpen(true)}
                  className="flex items-center gap-1.5 bg-violet-50 border border-violet-100 text-violet-600 rounded-lg px-3 py-2 text-xs font-semibold cursor-pointer hover:bg-violet-100 transition-colors whitespace-nowrap">
                  <Filter size={13} /> Filter
                  {filterPills.length > 0 && (
                    <span className="ml-0.5 w-4 h-4 rounded-full text-white text-[10px] flex items-center justify-center bg-violet-600">{filterPills.length}</span>
                  )}
                </button>
                <button onClick={() => setDrawerOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg cursor-pointer border-none hover:opacity-90 transition-all bg-[linear-gradient(135deg,#059669_0%,#10b981_100%)]">
                  <Plus size={13} /> Add Task
                </button>
                {selected.length > 0 && (
                  <>
                    <button onClick={handleCompleteSelected}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg cursor-pointer border-none hover:opacity-90 bg-[linear-gradient(135deg,#059669,#047857)]">
                      <CheckCheck size={13} /> Complete ({selected.length})
                    </button>
                    <button onClick={handleDeleteSelected}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg cursor-pointer border-none hover:opacity-90 bg-[linear-gradient(135deg,#ef4444,#dc2626)]">
                      <Trash2 size={13} /> Delete ({selected.length})
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Active filter pills */}
            {filterPills.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap px-4 py-2 border-b border-gray-50 bg-violet-50">
                <span className="text-xs font-bold text-slate-500">Active Filters:</span>
                {filterPills.map(p => (
                  <span key={p.key}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border cursor-pointer bg-violet-50 text-violet-600 border-violet-100">
                    {p.label}
                    <span onClick={() => removePill(p.key)} className="opacity-60 hover:opacity-100 cursor-pointer">×</span>
                  </span>
                ))}
                <button onClick={handleClearFilters}
                  className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 border-none bg-transparent cursor-pointer underline">
                  Clear All
                </button>
              </div>
            )}

            {/* Loading */}
            {loading ? (
              <div className="py-16 text-center">
                <div className="w-8 h-8 border-4 border-violet-100 border-t-violet-600 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-400">Loading tasks...</p>
              </div>
            ) : (
              <TodoListTab
                search={search}
                selected={selected}
                setSelected={setSelected}
                todos={todos}
                setTodos={setTodos}
                setConvertDraft={setConvertDraft}
                activeFilters={activeFilters}
              />
            )}
          </>
        )}

        {/* ── GROUPS TAB ── */}
        {activeTab === "Circles" && <GroupTab />}
      </div>
    </div>
  );
}